import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { TksEditFields } from './tksEditFields';
import {
  CustomsDeliveryCurrency,
  CustomsDeliveryVat,
} from '../../../../export-types/cleaned-types';
import { trpcSwr } from '../../../../api/trpc';
import { InputWrapper } from '../../../common/input-wrapper';
import { getCurrencySymbol } from '../../../../utils/common.utils';
import { Button } from 'rsuite';
import { CustomsDeliveryFindByNameSelect } from './customs-delivery-find-by-name-select';
import Select from 'react-select';

export type CalculatedTksData = {
  fee: { price: number; sum: number; info: string };
  tolls: { price: number; sum: number; info: string }[];
  nds: { price: number; sum: number; info: string };
  currency: CustomsDeliveryCurrency;
  unit?: string;
  total: number;
};

type CalculatorTksBlockProps = {
  setCalculatedData: (value?: CalculatedTksData) => void;
  setSelectedData: (value?: CalculateCustomsDeliveryFormType) => void;
  defaultValues?: Partial<CalculateCustomsDeliveryFormType>;
};
export type CalculateCustomsDeliveryFormType = {
  selectedCode?: {
    name: string;
    code: string;
    unit: string | null;
  };
  code: string;
  amount: number | null;
  price: number;
  vat: { value: CustomsDeliveryVat; label: string };
  currency: CustomsDeliveryCurrency;
};

const currencyTypes = [
  { value: 'cny', label: 'ЮАНЬ' },
  {
    value: 'usd',
    label: 'ДОЛЛАР',
  },
  { value: 'rub', label: 'РУБ' },
];

const CalculatorCustomsDeliveryBlock = ({
  defaultValues,
  setCalculatedData,
  setSelectedData,
}: CalculatorTksBlockProps) => {
  const intl = useIntl();

  const { trigger: calculate } =
    trpcSwr.customsDelivery.calculate.useSWRMutation();
  const form = useForm<CalculateCustomsDeliveryFormType>({
    defaultValues: {
      ...defaultValues,
      currency: CustomsDeliveryCurrency.cny,
    },
  });

  const selected = form.watch('selectedCode');
  const currency = form.watch('currency');
  const code = form.watch('code');
  const { data: good } = trpcSwr.customsDelivery.findByCode.useSWR(
    {
      search: selected?.code || '',
    },
    { isDisabled: !code },
  );

  const onSubmit = async (values: CalculateCustomsDeliveryFormType) => {
    setCalculatedData();
    setSelectedData(values);
    const result = await calculate({
      code: values.code,
      amount: values.amount || 0,
      currency: values.currency,
      vat: values.vat.value,
      price: values.price,
    });
    if (result) {
      setCalculatedData({
        ...result,
        currency: values.currency,
        unit: unitField?.short?.toLowerCase(),
      });
    }
  };

  const unitField = useMemo(() => {
    return TksEditFields()[
      good?.unit as keyof ReturnType<typeof TksEditFields>
    ];
  }, [good]);
  const vatTypes = useMemo<
    Array<{ value: CustomsDeliveryVat; label: string }>
  >(() => {
    if (!good?.vatTypes?.length) return [];
    return good.vatTypes.map((type) => ({
      value: type.value,
      label: type.text,
    }));
  }, [good]);

  const vat = form.watch('vat');

  useEffect(() => {
    if (good?.vatTypes.length && vat?.value) {
      const type = good.vatTypes?.find((type) => type.value === vat.value);
      if (type) {
        form.setValue('vat', { value: type.value, label: type.text });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [good]);

  return (
    <div>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <Controller
            control={form.control}
            rules={{
              required: intl.formatMessage({
                defaultMessage: 'Выберите товар',
                id: 'CalculatorCustomsDeliveryBlock.input-label.selectGood',
                description: 'Пояснение к полю ввода',
              }),
            }}
            name="selectedCode"
            render={({ field }) => (
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Наименование или код товара',
                  id: 'itemForm.input-label.goodsName',
                  description: 'Заголовок поля ввода',
                })}
                required={true}
                className="mb-2 w-100"
                error={form.formState.errors.selectedCode?.message}
              >
                <CustomsDeliveryFindByNameSelect
                  name={'customsDeliveryFindByNameSelect'}
                  selectedValue={field.value}
                  onChangeSelectedValue={(value) => {
                    field.onChange(value);
                    if (value) {
                      form.setValue('code', value.code);
                    }
                    setCalculatedData(undefined);
                  }}
                  placeholder={'Например, джинсы мужские'}
                />
                <div className="mt-1 fw-400 color-gray-400 fs-14">
                  <FormattedMessage
                    defaultMessage="Введите наименование товара или код ТН ВЭД"
                    id="ParcelItemForm.input-label.easyToFindOutTheOrderByTheNameOfTheGoods"
                    description="Пояснение к полю ввода"
                  />
                </div>
              </InputWrapper>
            )}
          />
        </div>
        <div className={'mb-2 flex-column gap-4 d-flex'}>
          <Controller
            control={form.control}
            name="code"
            render={({ field }) => (
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Код ТН ВЭД вашего товара',
                  id: 'itemForm.input-label.goodCodeTariff',
                  description: 'Заголовок поля ввода',
                })}
                className=" w-100 fs-14 color-gray-400"
              >
                <input
                  inputMode="text"
                  autoComplete="off"
                  type="text"
                  className={classNames(
                    ' w-100 rounded border p-1 text-left fs-14 color-gray-400 ',
                  )}
                  defaultValue={field.value || ''}
                  disabled={true}
                  data-testid="input-name"
                />
              </InputWrapper>
            )}
          />
        </div>

        <div className={'mb-2 d-flex flex-row gap-4 '}>
          <Controller
            control={form.control}
            name="currency"
            render={({ field }) => (
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Валюта расчета',
                  id: 'itemForm.input-label.currency',
                  description: 'Заголовок поля ввода',
                })}
                className="w-100"
                required={true}
              >
                <select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setCalculatedData();
                  }}
                  className="w-100 rounded border border-gray-300 p-2 text-left fs-14 "
                >
                  {currencyTypes.map((item) => {
                    return (
                      <option
                        value={item.value}
                        key={item.value}
                        className="mb-1  bg-white"
                      >
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </InputWrapper>
            )}
          />

          <Controller
            control={form.control}
            name="price"
            rules={{
              required: intl.formatMessage({
                defaultMessage: 'Укажите стоимость',
                id: 'CalculatorCustomsDeliveryBlock.input-label.setPrice',
                description: 'Пояснение к полю ввода',
              }),
            }}
            render={({ field }) => (
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Общая стоимость товаров',
                  id: 'itemForm.input-label.docs',
                  description: 'Заголовок поля ввода',
                })}
                className="w-100"
                required={true}
                error={form.formState.errors.price?.message}
              >
                <div className="relative mb-2 rounded d-flex gap-2">
                  <input
                    inputMode="text"
                    autoComplete="off"
                    type="text"
                    className={classNames(
                      'block w-full rounded border p-1 text-left',
                    )}
                    data-testid="input-name"
                    defaultValue={field.value}
                    onChange={(e) => {
                      field.onChange(+e.target.value);
                      setCalculatedData();
                    }}
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 d-flex flex-row items-center">
                    <span className=" color-gray-400 mt-1">
                      {getCurrencySymbol(currency)}
                    </span>
                  </div>
                </div>
              </InputWrapper>
            )}
          />
        </div>
        {unitField ? (
          <div className={'mb-2 d-flex gap-4'}>
            <Controller
              control={form.control}
              name="amount"
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите количество',
                  id: 'CalculatorCustomsDeliveryBlock.input-label.setAmount',
                  description: 'Пояснение к полю ввода',
                }),
              }}
              render={({ field }) => (
                <InputWrapper
                  title={intl.formatMessage(
                    {
                      defaultMessage: 'Количество, {value}',
                      id: 'itemForm.input-label.weightAndAmount',
                      description: 'Заголовок поля ввода',
                    },
                    { value: unitField.short },
                  )}
                  required={true}
                  className=" mb-2 w-100"
                  error={form.formState.errors.amount?.message}
                >
                  <div className="relative mb-2 rounded">
                    <input
                      type="number"
                      min={1}
                      step={1}
                      autoComplete="off"
                      className={classNames(
                        'border-gray-300',
                        ' w-100 rounded border p-1 text-left   outline-none',
                      )}
                      value={field.value || ''}
                      onChange={(e) => {
                        field.onChange(+e.target.value);
                        setCalculatedData();
                      }}
                      placeholder={unitField.short}
                      data-testid="input-name"
                    />
                  </div>
                </InputWrapper>
              )}
            />
          </div>
        ) : null}

        {vatTypes.length ? (
          <div className={'mb-2'}>
            <Controller
              control={form.control}
              name="vat"
              render={({ field }) => (
                <InputWrapper
                  title={intl.formatMessage({
                    defaultMessage: 'НДС',
                    id: 'itemForm.input-label.vat',
                    description: 'Заголовок поля ввода',
                  })}
                  className=" mb-2 w-100"
                  required={true}
                >
                  <Select
                    {...field}
                    options={vatTypes}
                    placeholder={'Выберите НДС'}
                    onChange={(v) => {
                      field.onChange(v);
                      setCalculatedData();
                    }}
                  />
                </InputWrapper>
              )}
            />
          </div>
        ) : null}
        <div>
          <Button
            as="button"
            type={'submit'}
            loading={form.formState.isSubmitting}
            className="btn btn-success"
            size={'lg'}
          >
            {' '}
            Рассчитать
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CalculatorCustomsDeliveryBlock;
